import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  nextTrainingId,
  trainingMode,
  TRAINING_MODE,
} from '../utils/training/trainingUtil';
import { SelectTrainingPageState } from './useSelectTraining';
import useTrainingState from './useTrainingState';

import { PAGE_PATH_NAME } from '../utils/const';

/**
 * Next ボタンのタイトル一覧
 */
const NEXT_BUTTON_TITLE = {
  NEXT_MODE: 'Next Mode',
  NEXT_TOPIC: 'Next Topic',
} as const;

type NEXT_BUTTON_TITLE =
  typeof NEXT_BUTTON_TITLE[keyof typeof NEXT_BUTTON_TITLE];

// 本カスタムフックの返り値
type UseTrainingCompleteValue = {
  // completeCheckboxの値
  completeCheckboxChecked: boolean;
  // completeCheckboxの値を変更する
  changeCompleteCheckbox: (checked: boolean) => void;
  // completeCheckbox変更時の処理
  onChangeCompleteCheckbox: (event: ChangeEvent<HTMLInputElement>) => void;
  // completeCheckboxの値が見えるか
  isCompleteCheckboxVisible: boolean;
  // Next ボタンのタイトル
  nextTrainingButtonTitle: string;
  // Next Mode / Next Topic ボタン押下時の処理
  nextTraining: () => void;
  // Topic Select ボタン押下時の処理
  topicSelect: () => void;
};

const useTrainingComplete = (): UseTrainingCompleteValue => {
  // CompleteCheckboxのcheckedの値
  const [completeCheckboxChecked, setCompleteCheckboxChecked] =
    useState<boolean>(false);
  // history
  const history = useHistory();
  // state
  const { trainingId, contents } = useTrainingState();

  /**
   * CompleteCheckboxを表示するかどうか
   * @returns true = modeがJump / false = modeがJump以外
   */
  const isCompleteCheckboxVisible = useMemo(() => {
    const mode = trainingMode(trainingId);

    return mode === TRAINING_MODE.JUMP;
  }, [trainingId]);

  /**
   * チェックボックスのステータスを変更する
   * @param checked
   */
  const changeCompleteCheckbox = useCallback((checked: boolean) => {
    setCompleteCheckboxChecked(checked);
  }, []);

  /**
   * コンプリートチェックボックスの値を変更イベント
   * @param event チェックボックスの変更イベント
   */
  const onChangeCompleteCheckbox = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      changeCompleteCheckbox(event.currentTarget.checked);
    },
    [changeCompleteCheckbox],
  );

  /**
   * Next Mode / Next Topic クリック時の動作
   * @param isComplete complete扱いにするか
   */
  const nextTraining = useCallback((): void => {
    const trainingState: SelectTrainingPageState = {
      trainingId: nextTrainingId(contents, trainingId),
      contents,
    };
    history.replace(PAGE_PATH_NAME.TRAINING, trainingState);
  }, [history, trainingId, contents]);

  /**
   * Topic Select クリック時の動作
   * @param isComplete complete扱いにするか
   */
  const topicSelect = useCallback((): void => {
    history.push(PAGE_PATH_NAME.LESSON);
  }, [history]);

  /**
   * Next Mode or Next Topic のタイトル
   */
  const nextTrainingButtonTitle = useMemo((): string => {
    switch (trainingMode(trainingId)) {
      case TRAINING_MODE.HOP:
      case TRAINING_MODE.SKIP:
        return NEXT_BUTTON_TITLE.NEXT_MODE;

      case TRAINING_MODE.JUMP:
        return NEXT_BUTTON_TITLE.NEXT_TOPIC;

      default:
        return NEXT_BUTTON_TITLE.NEXT_MODE;
    }
  }, [trainingId]);

  return {
    completeCheckboxChecked,
    changeCompleteCheckbox,
    onChangeCompleteCheckbox,
    isCompleteCheckboxVisible,
    nextTrainingButtonTitle,
    nextTraining,
    topicSelect,
  };
};

export default useTrainingComplete;
